import { Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type ReviewFeedbackNote,
  useAddReviewFeedback,
  useDeleteReviewFeedback,
  useFeedbackSentiments,
  useMyReviewFeedback,
  useUpdateReviewFeedback,
} from "@/hooks/use-review-feedback";
import { FEEDBACK_PROMPTS } from "@/lib/reviews/feedback-prompts";

/**
 * The written half of a review — EPIC-063.
 *
 * Notes rather than one box: a review is rarely all praise or all criticism,
 * so one tag on one paragraph would mean little and the reporting that groups
 * by tag would be weak. Each note is saved as it is added, and stays the
 * author's to change until the round closes.
 *
 * The unattributed comment is NOT saved here — it is held by the caller and
 * sent when they hand in. An autosaved anonymous draft is a row that says who
 * is writing it.
 */
export function ReviewFeedbackEditor({
  reviewerId,
  locked,
  criteria = [],
}: {
  reviewerId: string;
  locked: boolean;
  /** The reviewer's own form questions — EPIC-072, so a note can optionally tag one. */
  criteria?: { criterionId: string; name: string }[];
}) {
  const notes = useMyReviewFeedback(reviewerId);
  const sentiments = useFeedbackSentiments();
  const add = useAddReviewFeedback(reviewerId);
  const [drafting, setDrafting] = useState(false);

  const rows = notes.data ?? [];

  return (
    <div className="space-y-3">
      <div>
        <p className="font-medium">{FEEDBACK_PROMPTS.general}</p>
        <p className="mt-0.5 text-muted-foreground text-sm">
          Add as many notes as you like. Say how each is meant, so it can be read the way you
          intended.
        </p>
      </div>

      {rows.length === 0 && !drafting && (
        <p className="text-muted-foreground text-sm">Nothing written yet.</p>
      )}

      <ul className="space-y-2">
        {rows.map((note) => (
          <NoteRow
            key={note.id}
            note={note}
            reviewerId={reviewerId}
            locked={locked}
            criteria={criteria}
          />
        ))}
      </ul>

      {drafting ? (
        <NoteForm
          sentiments={sentiments.data ?? []}
          criteria={criteria}
          pending={add.isPending}
          onCancel={() => setDrafting(false)}
          onSave={(input) =>
            add.mutate(input, {
              onSuccess: () => setDrafting(false),
            })
          }
        />
      ) : (
        !locked && (
          <Button variant="outline" size="sm" onClick={() => setDrafting(true)}>
            <Plus className="size-4" />
            Add a note
          </Button>
        )
      )}
    </div>
  );
}

function NoteRow({
  note,
  reviewerId,
  locked,
  criteria,
}: {
  note: ReviewFeedbackNote;
  reviewerId: string;
  locked: boolean;
  criteria: { criterionId: string; name: string }[];
}) {
  const sentiments = useFeedbackSentiments();
  const update = useUpdateReviewFeedback(reviewerId);
  const remove = useDeleteReviewFeedback(reviewerId);
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <li className="rounded-lg border border-border p-3">
        <NoteForm
          sentiments={sentiments.data ?? []}
          criteria={criteria}
          pending={update.isPending}
          initial={{
            body: note.body,
            sentimentKey: note.sentimentKey,
            criterionId: note.criterionId,
          }}
          onCancel={() => setEditing(false)}
          onSave={(input) =>
            update.mutate({ id: note.id, ...input }, { onSuccess: () => setEditing(false) })
          }
        />
      </li>
    );
  }

  const taggedCriterion = criteria.find((c) => c.criterionId === note.criterionId);

  return (
    <li className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill tone="neutral" label={note.sentimentName} />
          {taggedCriterion && <StatusPill tone="info" label={taggedCriterion.name} />}
        </div>
        {!locked && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Edit this note"
              onClick={() => setEditing(true)}
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Remove this note"
              disabled={remove.isPending}
              onClick={() => remove.mutate(note.id)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        )}
      </div>
      <p className="mt-2 whitespace-pre-line text-sm">{note.body}</p>
    </li>
  );
}

function NoteForm({
  sentiments,
  criteria,
  pending,
  initial,
  onSave,
  onCancel,
}: {
  sentiments: { key: string; name: string; description: string | null }[];
  criteria: { criterionId: string; name: string }[];
  pending: boolean;
  initial?: { body: string; sentimentKey: string; criterionId?: string | null };
  onSave: (input: { body: string; sentimentKey: string; criterionId: string | null }) => void;
  onCancel: () => void;
}) {
  const [body, setBody] = useState(initial?.body ?? "");
  const [sentimentKey, setSentimentKey] = useState(initial?.sentimentKey ?? "");
  const [criterionId, setCriterionId] = useState(initial?.criterionId ?? "__none");
  const ready = body.trim().length > 0 && sentimentKey.length > 0;

  return (
    <div className="space-y-2 rounded-lg border border-border border-dashed p-3">
      <div className="flex flex-wrap gap-2">
        <Select value={sentimentKey} onValueChange={setSentimentKey}>
          <SelectTrigger className="w-full sm:w-64" aria-label="How this is meant">
            <SelectValue placeholder="How is this meant?" />
          </SelectTrigger>
          <SelectContent>
            {sentiments.map((s) => (
              <SelectItem key={s.key} value={s.key}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {criteria.length > 0 && (
          <Select value={criterionId} onValueChange={setCriterionId}>
            <SelectTrigger className="w-full sm:w-64" aria-label="Which question this is about">
              <SelectValue placeholder="Which question is this about?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">General — not about one question</SelectItem>
              {criteria.map((c) => (
                <SelectItem key={c.criterionId} value={c.criterionId}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="What happened, and what difference did it make?"
        rows={3}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!ready || pending}
          onClick={() =>
            onSave({
              body,
              sentimentKey,
              criterionId: criterionId === "__none" ? null : criterionId,
            })
          }
        >
          {pending ? "Saving…" : "Save note"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="size-4" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
